import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useScrollRestoration } from "@src/appV2/lib/utils/useScrollRestoration";
import type { HiringFeedsResponse } from "@src/appV2/redesign/HiringFeed/api/hiringFeed.schema";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { JobsApplicationsTabs } from "@src/appV2/redesign/Placements/components/JobsApplicationsTabs";
import { useMaxReferralBonusAmount } from "@src/appV2/Referral/Bonus/flags";
import { WORKER_REFERRALS_SHARE_LINK_PATH_FULL } from "@src/appV2/Referral/path";
import { type Worker } from "@src/appV2/Worker/api/types";
import { type UseInfiniteQueryResult } from "@tanstack/react-query";

import { Button } from "../../components/Button";
import { OnboardingRequirementsBanner } from "../../PlacementCandidate/components/OnboardingRequirementsBanner";
import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { useIsHiringReferralEnabled } from "../useIsHiringReferralEnabled";
import { AppliedPlacementListView } from "./AppliedPlacementListView";
import { OpenPlacementListView } from "./OpenPlacementListView";
import { RestrictedAccessBanner } from "./RestrictedAccessBanner";

interface PlacementsListContainerProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
  filter: GetPlacementsForWorkplaceQuery["filter"];
  setFilter: (filter: GetPlacementsForWorkplaceQuery["filter"]) => void;
  hiringFeedResults: UseInfiniteQueryResult<HiringFeedsResponse>;
  showApplications: boolean;
  setShowApplications: (value: boolean) => void;
}

export function PlacementsListContainer(props: PlacementsListContainerProps) {
  const {
    placementCandidate,
    worker,
    filter,
    setFilter,
    showApplications,
    setShowApplications,
    hiringFeedResults,
  } = props;

  useScrollRestoration({ key: "jobs-tab", containerId: "jobs-list-container" });

  const maxReferralBonusAmount = useMaxReferralBonusAmount("postOnboardingBonusInDollars");
  const isHiringReferralEnabled = useIsHiringReferralEnabled();

  return (
    <Stack
      sx={{
        paddingY: 5,
        backgroundColor: (theme) => theme.palette.background.tertiary,
        flex: 1,
      }}
      id="jobs-list-container"
    >
      <Stack>
        <Stack spacing={6} sx={{ paddingX: 5 }}>
          {isHiringReferralEnabled && (
            <Stack justifyContent="flex-end" direction="row">
              <Button
                startIconType="share"
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: (theme) => theme.palette.intent?.success.background,
                  color: (theme) => theme.palette.intent?.success.text,
                }}
                href={WORKER_REFERRALS_SHARE_LINK_PATH_FULL}
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.PLACEMENT_PAGE_REFER_FRIEND_CLICKED, {
                    placementCandidateId: placementCandidate.id,
                    workerId: worker.userId,
                  });
                }}
              >
                Refer friend, earn ${maxReferralBonusAmount}
              </Button>
            </Stack>
          )}
          {placementCandidate.isRestricted && placementCandidate.mostRecentBan ? (
            <RestrictedAccessBanner
              placementCandidateBan={placementCandidate.mostRecentBan}
              workerTimezone={worker.tmz}
            />
          ) : undefined}
          <JobsApplicationsTabs
            showApplications={showApplications}
            onChange={(showApplications) => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_CANDIDATE_JOBS_TAB_SWITCHED, {
                placementCandidateId: placementCandidate.id,
                workerId: worker.userId,
                showApplications,
              });
              setShowApplications(showApplications);
            }}
          />

          <OnboardingRequirementsBanner workerId={worker.userId} />

          {!showApplications && (
            <OpenPlacementListView
              placementCandidate={placementCandidate}
              worker={worker}
              filter={filter}
              setFilter={setFilter}
              showApplications={showApplications}
              hiringFeedResults={hiringFeedResults}
            />
          )}

          {showApplications && (
            <AppliedPlacementListView
              placementCandidate={placementCandidate}
              worker={worker}
              filter={filter}
              showApplications={showApplications}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
